import React, { useEffect, useRef, useState } from 'react';
import SearchableSelect from '../../shared/SearchableSelect';
import { getMemberCategoryOptions } from '../../api/api';

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' }
];

const RELATION_TYPE_OPTIONS = [
  { value: 'guardian', label: 'Guardian' },
  { value: 'father', label: 'Father' },
  { value: 'mother', label: 'Mother' }
];

// Same-render fallback only, shown until getMemberCategoryOptions (the live
// DB enum, see memberModel.getMemberCategoryOptions) resolves — the actual
// list of selectable member types always comes from that call.
const FALLBACK_MEMBER_TYPES = [
  { value: 'school_parent', label: 'School Parent' },
  { value: 'outsider', label: 'Outsider' }
];

// Purely decorative — a category with no entry here still renders (with a
// generic icon) rather than being hidden, so this never gates which
// categories are selectable.
const MEMBER_TYPE_ICONS = {
  school_parent: 'fa-user-graduate',
  outsider: 'fa-id-card'
};
const DEFAULT_MEMBER_TYPE_ICON = 'fa-id-badge';

// Pure presentational form — all state, validation and submit logic lives in
// useRegisterMemberWorkflow so the Members-page modal and the
// backward-compatible full-page route render the exact same fields without
// duplicating any of that logic.
const RegisterMemberForm = ({
  formId,
  form,
  onChange,
  onMemberSourceChange,
  isSchoolParent,
  onSubmit,
  profileImageFile,
  onProfileImageChange,
  outsiderDepositAmount
}) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [memberTypes, setMemberTypes] = useState(FALLBACK_MEMBER_TYPES);
  const typeButtonRefs = useRef([]);

  useEffect(() => {
    if (!profileImageFile) {
      setPreviewUrl(null);
      return;
    }

    const url = URL.createObjectURL(profileImageFile);
    setPreviewUrl(url);

    return () => URL.revokeObjectURL(url);
  }, [profileImageFile]);

  useEffect(() => {
    let active = true;
    const result = getMemberCategoryOptions();
    // Guards against a non-promise return (e.g. an incomplete test mock) —
    // the real getMemberCategoryOptions always returns a promise.
    if (result && typeof result.then === 'function') {
      result
        .then((options) => {
          if (active && Array.isArray(options) && options.length) setMemberTypes(options);
        })
        .catch(() => {});
    }
    return () => { active = false; };
  }, []);

  // Standard ARIA radiogroup keyboard behaviour: Left/Right/Up/Down moves
  // selection to the adjacent option and moves focus with it, so keyboard
  // users get the same "pick one of two" affordance as a native radio group.
  const handleTypeKeyDown = (e, index) => {
    if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(e.key)) return;

    e.preventDefault();
    const direction = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = (index + direction + memberTypes.length) % memberTypes.length;

    onMemberSourceChange(memberTypes[nextIndex].value);
    typeButtonRefs.current[nextIndex]?.focus();
  };

  return (
    <form id={formId} onSubmit={onSubmit} noValidate>
      <div className="rm-section">
        <div className="rm-section-title">Member Type</div>
        <div className="rm-type-toggle" role="radiogroup" aria-label="Member Type">
          {memberTypes.map((type, index) => {
            const isActive = form.member_source === type.value;

            return (
              <button
                key={type.value}
                ref={(el) => { typeButtonRefs.current[index] = el; }}
                type="button"
                role="radio"
                aria-checked={isActive}
                className={`rm-type-toggle__btn${isActive ? ' rm-type-toggle__btn--active' : ''}`}
                onClick={() => onMemberSourceChange(type.value)}
                onKeyDown={(e) => handleTypeKeyDown(e, index)}
              >
                <i className={`fas ${MEMBER_TYPE_ICONS[type.value] || DEFAULT_MEMBER_TYPE_ICON}`} aria-hidden="true" />
                <span className="rm-type-toggle__label">{type.label}</span>
                {isActive && <i className="fas fa-check-circle rm-type-toggle__check" aria-hidden="true" />}
              </button>
            );
          })}
        </div>

        {form.member_source ? (
          <div className="rm-type-toggle__status">
            Selected member type: <strong>{memberTypes.find((t) => t.value === form.member_source)?.label || form.member_source}</strong>
          </div>
        ) : (
          <div className="rm-type-toggle__status rm-type-toggle__status--error">
            Please select a member type.
          </div>
        )}

        {form.member_source === 'outsider' && outsiderDepositAmount > 0 && (
          <div className="rm-type-toggle__deposit-note">
            ₹{outsiderDepositAmount} security deposit required
          </div>
        )}
      </div>

      <div className="rm-section">
        <div className="rm-section-title">Personal Information</div>
        <div className="fine-member-grid">
          <div className="fine-field fine-field--full rm-photo-field">
            <label className="form-label">Photo</label>
            <div className="rm-photo-row">
              {previewUrl && (
                <img src={previewUrl} alt="Profile preview" className="rm-photo-preview" />
              )}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="form-control"
                onChange={onProfileImageChange}
              />
            </div>
          </div>
          <div className="fine-field">
            <label className="form-label">First Name *</label>
            <input
              type="text"
              name="first_name"
              className="form-control"
              value={form.first_name}
              onChange={onChange}
              required
            />
          </div>
          <div className="fine-field">
            <label className="form-label">Last Name</label>
            <input
              type="text"
              name="last_name"
              className="form-control"
              value={form.last_name}
              onChange={onChange}
            />
          </div>
          <div className="fine-field">
            <label className="form-label">Mobile *</label>
            <input
              type="text"
              name="phone"
              className="form-control"
              value={form.phone}
              onChange={onChange}
              inputMode="numeric"
              required
            />
          </div>
          <div className="fine-field">
            <label className="form-label">Email</label>
            <input
              type="email"
              name="email"
              className="form-control"
              value={form.email}
              onChange={onChange}
            />
          </div>
          <div className="fine-field">
            <label className="form-label">Password *</label>
            <input
              type="password"
              name="password"
              className="form-control"
              value={form.password}
              onChange={onChange}
              required
            />
          </div>
          <div className="fine-field">
            <label className="form-label">Gender</label>
            <SearchableSelect
              name="gender"
              value={form.gender}
              onChange={onChange}
              options={GENDER_OPTIONS}
              placeholder="Select Gender"
            />
          </div>
          <div className="fine-field">
            <label className="form-label">Date of Birth</label>
            <input
              type="date"
              name="dob"
              className="form-control"
              value={form.dob}
              onChange={onChange}
              max={new Date().toISOString().slice(0, 10)}
            />
          </div>
        </div>
      </div>

      {isSchoolParent && (
        <div className="rm-section">
          <div className="rm-section-title">Parent / Student Information</div>
          <div className="fine-member-grid">
            <div className="fine-field">
              <label className="form-label">Admission Number *</label>
              <input
                type="text"
                name="admission_no"
                className="form-control"
                value={form.admission_no}
                onChange={onChange}
                required
              />
            </div>
            <div className="fine-field">
              <label className="form-label">Relation to Student *</label>
              <SearchableSelect
                name="relation_type"
                value={form.relation_type}
                onChange={onChange}
                options={RELATION_TYPE_OPTIONS}
                placeholder="Select Relation"
                required
                clearable={false}
              />
            </div>
          </div>
        </div>
      )}

      <div className="rm-section">
        <div className="rm-section-title">Address Information</div>
        <div className="fine-member-grid">
          <div className="fine-field fine-field--full">
            <label className="form-label">Address</label>
            <textarea name="address" rows="2" className="form-control" value={form.address} onChange={onChange} />
          </div>
          <div className="fine-field">
            <label className="form-label">City</label>
            <input type="text" name="city" className="form-control" value={form.city} onChange={onChange} />
          </div>
          <div className="fine-field">
            <label className="form-label">State</label>
            <input type="text" name="state" className="form-control" value={form.state} onChange={onChange} />
          </div>
          <div className="fine-field">
            <label className="form-label">Country</label>
            <input type="text" name="country" className="form-control" value={form.country} onChange={onChange} />
          </div>
          <div className="fine-field">
            <label className="form-label">Pincode</label>
            <input
              type="text"
              name="pincode"
              className="form-control"
              value={form.pincode}
              onChange={onChange}
              inputMode="numeric"
              maxLength={6}
            />
          </div>
        </div>
      </div>
    </form>
  );
};

export default RegisterMemberForm;
